import { Link } from "react-router-dom";
import { ArrowRight, CircleDollarSign } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { isFreeBuyerUser } from "@/lib/access";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import { orgScopedPath } from "@/lib/orgDoor";
import { Button } from "@/components/ui/button";

/**
 * "Pre-qualify for this loan" — the bridge from sizing a loan (SBA calculator,
 * a listing's debt-service estimate) to the Funding Marketplace.
 *
 * Renders nothing unless the viewer is a paying buyer with the Funding
 * Marketplace switched on: the marketplace sits behind MembersOnlyGate and the
 * module switchboard, and a link into a gate or a hidden module is a dead end.
 *
 * `listingId` rides along as ?listing=, so the inquiry names the deal.
 */
export default function FundingCta({ listingId, className }: { listingId?: string; className?: string }) {
  const { user, hasRole } = useAuth();
  const moduleOn = useModuleVisibility();

  if (!user || !hasRole("buyer") || isFreeBuyerUser(user) || !moduleOn("funding_marketplace")) return null;

  const href =
    orgScopedPath("/buyer/funding", user.org_slug) + (listingId ? `?listing=${encodeURIComponent(listingId)}` : "");

  return (
    <div
      className={`flex flex-wrap items-center gap-4 rounded-2xl border border-primary/30 bg-primary/8 px-5 py-4 ${className ?? ""}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <CircleDollarSign className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-foreground">
          {listingId ? "Get financing for this deal" : "Ready to pre-qualify?"}
        </p>
        <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
          Send one inquiry to a funding partner. No obligation; the lender contacts you directly.
        </p>
      </div>
      <Link to={href} className="shrink-0">
        <Button type="button" size="sm" className="gap-1.5">
          Funding Marketplace <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </Link>
    </div>
  );
}
